import each from 'lodash/each';
import get from 'lodash/get';
import set from 'lodash/set';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import omitBy from 'lodash/omitBy';
import request from 'superagent';
import * as Sentry from '@sentry/browser';

export const FIELDS_TYPES = {
  SINGLE_SELECT: 'SINGLE_SELECT',
  MULTI_SELECT: 'MULTI_SELECT',
  TEXT_FIELDS: 'TEXT_FIELDS',
  SUB_FIELDS: 'SUB_FIELDS',
  ADDRESS: 'ADDRESS',
};

export function loadScript(src) {
  return new Promise((resolve, reject) => {
    let s;
    s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

/**
 * @param {Object} cardElement A Stripe "card" type element.
 * @param {String} [cardName] The card holder name.
 * @param {String} [cardAddressLine1] The card holder address line 1.
 * @param {String} [cardAddressCity] The card holder city.
 * @param {String} [cardAddressState] The card holder state.
 * https://stripe.com/docs/elements/reference#the-elements-object
 */
export function createStripeTokenFromElement(
  cardElement,
  cardName,
  cardAddressLine1,
  cardAddressCity,
  cardAddressState
) {
  return new Promise((resolve, reject) => {
    window.stripe
      .createToken(cardElement, {
        name: cardName,
        address_line1: cardAddressLine1,
        address_city: cardAddressCity,
        address_state: cardAddressState,
      })
      .then((result) => {
        if (result.error) {
          reject(result.error);
        } else {
          resolve(result.token.id);
        }
      });
  });
}

/**
 * @param {Object} opts Payment method arguments.
 * @param {String} opts.firstName User first name.
 * @param {String} opts.lastName User last name.
 * @param {String} opts.email User email.
 * @param {String} opts.stripeToken The Stripe payment token.
 * @param {String} opts.apiEndpoint The Sure api url to post to.
 * @param {String} [opts.authToken] The Sure authentication token.
 * @param {Boolean} [opts.getId] If true, it will resolve with the payment
 * method ID instead of the entire body JSON.
 */
export function createPaymentMethod(opts) {
  const payload = {
    first_name: opts.firstName,
    last_name: opts.lastName,
    email: opts.email,
    token: opts.stripeToken,
  };

  const req = request.post(opts.apiEndpoint);

  if (opts.authToken) {
    const authMethod = opts.authToken.length < 60 ? 'Token' : 'Bearer';
    req.set('Authorization', `${authMethod} ${opts.authToken}`);
  }

  return new Promise((resolve, reject) => {
    req.send(payload).end((err, response) => {
      if (err) {
        reject(err);
      } else if (opts.getId) {
        const paymentMethodId =
          response.body.public_id || response.body.payment_method_id;
        resolve(paymentMethodId);
      } else {
        resolve(response.body);
      }
    });
  });
}

export function logException(ex, context) {
  Sentry.withScope((scope) => {
    if (!isEmpty(context) && isPlainObject(context)) {
      each(context, (val, key) => {
        scope.setExtra(key, val);
      });
    }

    if (get(ex, '_hrq')) {
      scope.setTag('hrq', ex._hrq);
    }

    if (ex instanceof Error) {
      Sentry.captureException(ex);
    } else if (isPlainObject(ex) && ex.message) {
      Sentry.captureException(new Error(ex.message));
    } else if (isString(ex)) {
      Sentry.captureMessage(ex);
    }
  });
  window.console && window.console.error && window.console.error(ex);
}

/**
 * Omit unneccessary or sensitive data from the Redux state.
 *
 * @param {Object} state The redux store state object.
 * @returns {Object} The sanitized state object.
 */
export function sanitizeSentryStatePayload(state = {}) {
  const omitKeys = [
    'airlines',
    'authToken',
    'clients',
    'policies',
    'policies',
    'quotes',
    'routing',
  ];
  return omitBy(state, (val, key) => {
    return val === null || includes(omitKeys, key);
  });
}

/**
 * Returns a new promise which resolves the axios response body or rejects with the error data.
 */
export function destructureAxiosReq(axiosPromise) {
  return new Promise((resolve, reject) => {
    axiosPromise.then(
      (response) => resolve(response.data),
      (error) => {
        if (error.response) {
          if (get(error, 'response.headers')) {
            set(
              error,
              'response.data.error._hrq',
              error.response.headers['x-request-id']
            );
          }
          reject(get(error, 'response.data.error'));
        } else {
          reject(error);
        }
      }
    );
  });
}
